import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Anchor, ChevronRight, ChevronLeft, CheckCircle2, Ship, MapPin, User, Loader2,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { VESSEL_TYPES, FREQUENCIES, REGIONS, TIERS, BRAND } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STEPS = [
  { n: 1, label: "Vessel", icon: Ship },
  { n: 2, label: "Location", icon: MapPin },
  { n: 3, label: "Contact", icon: User },
];

const PLAN_OPTIONS = [...TIERS.map((t) => `${t.name} ($${t.price}/mo)`), "Not sure yet"];

const empty = {
  vessel_type: "", vessel_length: "", service_region: "", marina_location: "",
  preferred_plan: "", frequency: "", name: "", email: "", phone: "", message: "",
  company: "",
};

export default function RequestInspection() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  const validStep = () => {
    if (step === 1) return form.vessel_type && form.vessel_length.trim();
    if (step === 2) return form.service_region && form.marina_location.trim();
    if (step === 3) return form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 3;
    return true;
  };

  const next = () => {
    if (!validStep()) {
      toast.error("Please complete the required fields to continue.");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validStep()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/inspection", form);
      setDone(true);
      toast.success("Inspection request received.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900 px-6 pt-20" data-testid="inspection-success">
        <div className="max-w-lg text-center">
          <div className="h-20 w-20 mx-auto rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center">
            <CheckCircle2 className="h-10 w-10 text-gold-500" />
          </div>
          <h1 className="mt-7 text-3xl sm:text-4xl font-serif font-bold text-white">Request received</h1>
          <p className="mt-4 text-slate-300">
            Thank you, {form.name.split(" ")[0] || "Captain"}. Our team will review your
            vessel details and follow up shortly at <span className="text-gold-400">{form.email}</span> to
            confirm scope and scheduling.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/")} className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-7 py-3.5 rounded-full transition-colors" data-testid="success-home-button">
              Back to Home
            </button>
            <button onClick={() => navigate("/pricing")} className="border border-white/25 text-white hover:border-gold-500/60 font-semibold px-7 py-3.5 rounded-full transition-colors">
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory dark:bg-navy-900 min-h-screen" data-testid="request-inspection-page">
      <Seo title="Request an Inspection — Hussar Marine Concierge" description="Request a dockside vessel inspection in Southwest Florida. Share your vessel and marina details and we'll follow up to confirm scope and scheduling." />

      <section className="pt-32 pb-12 bg-navy-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="eyebrow text-gold-400 mb-4">Begin Your Service</p>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold text-white">Request an Inspection</h1>
            <p className="mt-4 text-slate-300">A few details about your vessel and marina — no payment required.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-2xl mx-auto px-6">
          {/* stepper */}
          <div className="flex items-center justify-between mb-10">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-2">
                  <div className={`h-11 w-11 rounded-full flex items-center justify-center border-2 transition-colors ${
                    step >= s.n ? "bg-gold-500 border-gold-500 text-navy-900" : "border-slate-300 dark:border-white/20 text-slate-400"
                  }`}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className={`text-xs font-medium ${step >= s.n ? "text-navy-900 dark:text-white" : "text-slate-400"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mb-5 ${step > s.n ? "bg-gold-500" : "bg-slate-200 dark:bg-white/10"}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gold-500/20 p-7 sm:p-9 shadow-xl">
            {/* honeypot */}
            <input
              type="text" tabIndex={-1} autoComplete="off"
              value={form.company} onChange={setE("company")}
              className="hidden" aria-hidden="true"
            />

            {step === 1 && (
              <div className="space-y-6" data-testid="step-vessel">
                <h2 className="font-serif text-2xl text-navy-900 dark:text-white">About your vessel</h2>
                <Field label="Vessel type *">
                  <Select value={form.vessel_type} onValueChange={set("vessel_type")}>
                    <SelectTrigger data-testid="input-vessel-type"><SelectValue placeholder="Select a type" /></SelectTrigger>
                    <SelectContent>
                      {VESSEL_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Vessel length (ft) *">
                  <Input value={form.vessel_length} onChange={setE("vessel_length")} placeholder="e.g. 42 ft" data-testid="input-vessel-length" />
                </Field>
                <Field label="Preferred plan">
                  <Select value={form.preferred_plan} onValueChange={set("preferred_plan")}>
                    <SelectTrigger data-testid="input-preferred-plan"><SelectValue placeholder="Optional — select a plan" /></SelectTrigger>
                    <SelectContent>
                      {PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="step-location">
                <h2 className="font-serif text-2xl text-navy-900 dark:text-white">Where is your vessel?</h2>
                <Field label="Service region *">
                  <Select value={form.service_region} onValueChange={set("service_region")}>
                    <SelectTrigger data-testid="input-service-region"><SelectValue placeholder="Select a region" /></SelectTrigger>
                    <SelectContent>
                      {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Marina / dock location *">
                  <Input value={form.marina_location} onChange={setE("marina_location")} placeholder="Marina name or dock address" data-testid="input-marina-location" />
                </Field>
                <Field label="Preferred inspection frequency">
                  <Select value={form.frequency} onValueChange={set("frequency")}>
                    <SelectTrigger data-testid="input-frequency"><SelectValue placeholder="Optional — select frequency" /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="step-contact">
                <h2 className="font-serif text-2xl text-navy-900 dark:text-white">How can we reach you?</h2>
                <Field label="Full name *">
                  <Input value={form.name} onChange={setE("name")} placeholder="Your name" data-testid="input-name" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email *">
                    <Input type="email" value={form.email} onChange={setE("email")} placeholder="you@email.com" data-testid="input-email" />
                  </Field>
                  <Field label="Phone *">
                    <Input value={form.phone} onChange={setE("phone")} placeholder="(239) 555-0100" data-testid="input-phone" />
                  </Field>
                </div>
                <Field label="Anything else we should know?">
                  <Textarea value={form.message} onChange={setE("message")} rows={4} placeholder="Access notes, specific concerns, timing…" data-testid="input-message" />
                </Field>
              </div>
            )}

            <div className="mt-9 flex items-center justify-between gap-4">
              {step > 1 ? (
                <button onClick={back} className="inline-flex items-center gap-1.5 text-navy-900 dark:text-white font-semibold px-5 py-3 rounded-full border border-navy-900/15 dark:border-white/15 hover:border-gold-500 transition-colors" data-testid="step-back-button">
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
              ) : <span />}

              {step < 3 ? (
                <button onClick={next} className="inline-flex items-center gap-1.5 bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900 font-semibold px-7 py-3 rounded-full hover:opacity-90 transition-opacity" data-testid="step-next-button">
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-navy-900 font-semibold px-7 py-3 rounded-full transition-colors" data-testid="submit-inspection-button">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Anchor className="h-4 w-4" />}
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
            By submitting you agree to be contacted about your request. No payment is collected with this request. See our{" "}
            <a href="/privacy" className="underline hover:text-gold-500">Privacy Notice</a>.
          </p>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-navy-900 dark:text-slate-200">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
